/**
 * Auto Repair - Services Report (Task 63)
 * Revenue analysis, turnaround time, and common services
 */

import { useEffect, useMemo, useState } from 'react';
import { useIndustryWorkOrders } from '@/hooks/industry';
import { CompanyType } from '@/types/company';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarSign, TrendingUp, Clock, Wrench } from 'lucide-react';
import { parseISO, differenceInDays, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, isWithinInterval, subMonths } from 'date-fns';

type PeriodFilter = 'current_month' | 'last_month' | 'quarter' | 'year' | 'all';

interface ServiceStats {
  diagnosis: string;
  count: number;
  totalRevenue: number;
  avgRevenue: number;
  avgTurnaround: number;
}

export default function AutoRepairServicesReportPage() {
  useEffect(() => {
    document.title = 'Relatório de Serviços | Controle Financeiro';
  }, []);

  const { workOrders, isLoading } = useIndustryWorkOrders({ domain: CompanyType.AUTO_REPAIR });
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('current_month');

  // Get date range for filter
  const getDateRange = (period: PeriodFilter): { start: Date; end: Date } | null => {
    const now = new Date();
    switch (period) {
      case 'current_month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      case 'quarter':
        return { start: startOfQuarter(now), end: endOfQuarter(now) };
      case 'year':
        return { start: startOfYear(now), end: endOfYear(now) };
      case 'all':
        return null;
      default:
        return { start: startOfMonth(now), end: endOfMonth(now) };
    }
  };

  // Filter work orders by period
  const filteredWorkOrders = useMemo(() => {
    if (!workOrders) return [];

    const dateRange = getDateRange(periodFilter);
    if (!dateRange) return workOrders;

    return workOrders.filter((wo) => {
      if (!wo.start_date) return false;
      const woDate = parseISO(wo.start_date);
      return isWithinInterval(woDate, { start: dateRange.start, end: dateRange.end });
    });
  }, [workOrders, periodFilter]);

  // Calculate statistics
  const stats = useMemo(() => {
    const serviceMap = new Map<string, ServiceStats>();
    let totalRevenue = 0;
    let completedCount = 0;
    let totalTurnaround = 0;

    filteredWorkOrders.forEach((wo) => {
      const laborCost = (wo.metadata?.laborCost as number) || 0;
      const partsCost = (wo.metadata?.partsCost as number) || 0;
      const revenue = laborCost + partsCost;
      const diagnosis = (wo.metadata?.diagnosis as string) || 'Sem diagnóstico';

      totalRevenue += revenue;

      // Calculate turnaround time for completed orders
      if (wo.status === 'completed' && wo.start_date) {
        const turnaround = differenceInDays(new Date(), parseISO(wo.start_date));
        totalTurnaround += turnaround;
        completedCount++;
      }

      // Aggregate by diagnosis/service type
      const existing = serviceMap.get(diagnosis);
      if (existing) {
        existing.count++;
        existing.totalRevenue += revenue;
        existing.avgRevenue = existing.totalRevenue / existing.count;
      } else {
        serviceMap.set(diagnosis, {
          diagnosis,
          count: 1,
          totalRevenue: revenue,
          avgRevenue: revenue,
          avgTurnaround: 0,
        });
      }
    });

    const avgTurnaround = completedCount > 0 ? totalTurnaround / completedCount : 0;
    const serviceStats = Array.from(serviceMap.values()).sort((a, b) => b.totalRevenue - a.totalRevenue);

    return {
      totalRevenue,
      totalOrders: filteredWorkOrders.length,
      completedOrders: completedCount,
      avgTurnaround,
      serviceStats,
    };
  }, [filteredWorkOrders]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Relatório de Serviços</h1>
        <p className="text-muted-foreground">Análise financeira e operacional</p>
      </div>

      {/* Period Filter */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-xs">
            <Select value={periodFilter} onValueChange={(v: PeriodFilter) => setPeriodFilter(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="current_month">Mês Atual</SelectItem>
                <SelectItem value="last_month">Mês Anterior</SelectItem>
                <SelectItem value="quarter">Trimestre Atual</SelectItem>
                <SelectItem value="year">Ano Atual</SelectItem>
                <SelectItem value="all">Todo o Período</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Receita Total
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.totalOrders} ordens de serviço
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Ticket Médio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(stats.totalOrders > 0 ? stats.totalRevenue / stats.totalOrders : 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Por ordem de serviço
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Tempo Médio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{Math.round(stats.avgTurnaround)} dias</div>
            <p className="text-xs text-muted-foreground mt-1">
              Prazo de execução
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              Ordens Concluídas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completedOrders}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.totalOrders > 0 ? ((stats.completedOrders / stats.totalOrders) * 100).toFixed(0) : 0}% do total
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Services Table */}
      <Card>
        <CardHeader>
          <CardTitle>Serviços Mais Comuns</CardTitle>
          <CardDescription>Ranking por receita gerada</CardDescription>
        </CardHeader>
        <CardContent>
          {stats.serviceStats.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Nenhum dado disponível para o período selecionado
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Tipo de Serviço</TableHead>
                    <TableHead className="text-right">Quantidade</TableHead>
                    <TableHead className="text-right">Receita Total</TableHead>
                    <TableHead className="text-right">% do Total</TableHead>
                    <TableHead className="text-right">Ticket Médio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.serviceStats.map((service, index) => {
                    const percentage = stats.totalRevenue > 0 ? (service.totalRevenue / stats.totalRevenue) * 100 : 0;
                    return (
                      <TableRow key={service.diagnosis}>
                        <TableCell className="font-medium">{index + 1}</TableCell>
                        <TableCell>{service.diagnosis}</TableCell>
                        <TableCell className="text-right">{service.count}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(service.totalRevenue)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm text-muted-foreground">
                            {percentage.toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(service.avgRevenue)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
